/** Waits before the retries of a failed call; it is given up after the last. */
export const RETRY_DELAYS_MS = [5000, 15_000, 60_000]

/**
 * When the analytics queue flushes next (FE-321): the earliest delay asked
 * for, or the backoff after a failed call, which nothing shortens until it
 * fires. Failed attempts are counted per call by the caller, so calls that
 * fail together (a hidden-page flush) each keep their whole retry budget.
 */
export class FlushTimer {
  private timer: {
    handle: ReturnType<typeof setTimeout>
    due: number
    backoff: boolean
  } | null = null

  constructor(private readonly onFire: () => void) {}

  get isBackingOff() {
    return Boolean(this.timer?.backoff)
  }

  /** Fires after `delay`, unless an earlier fire or a backoff is due. */
  start(delay: number) {
    if (this.isBackingOff) return
    if (!this.timer || this.timer.due > Date.now() + delay) this.set(delay)
  }

  /**
   * Holds the next fire for the backoff after the `failures`-th failed
   * attempt of `what`; `false`, with a warning, once its retries are used up.
   */
  retry(failures: number, what: string) {
    if (failures > RETRY_DELAYS_MS.length) {
      console.warn(`analytics: ${what} given up`)
      return false
    }

    const delay = RETRY_DELAYS_MS[failures - 1]
    if (!this.isBackingOff || (this.timer?.due ?? 0) < Date.now() + delay) {
      this.set(delay, true)
    }
    return true
  }

  private set(delay: number, backoff = false) {
    if (this.timer) clearTimeout(this.timer.handle)

    const handle = setTimeout(() => {
      this.timer = null
      this.onFire()
    }, delay)
    this.timer = { handle, due: Date.now() + delay, backoff }
  }
}
